import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Text, VStack } from '@chakra-ui/react';
import {
  updatesGlobalDate,
  useAppDispatch,
  useAppSelector,
  useGetRepairChartDataQuery,
  useLazyGetRepairTableDataQuery,
} from '@spartalabs/pdc-core';
import { BarChart, MultiInfoCard } from 'components';
import { ChartCard } from 'components';
import TableCard from 'components/Cards/TableCard';
import { DetailsModal } from 'components/DataTable/DetailsModal';
import { ItensModal } from 'components/DataTable/ItensModal';
import format from 'date-fns/format';
import { useChart } from 'hooks/useChart';
import { DelayAnimation } from 'shared/DelayAnimation';
import { ChartTypeFilters, emptyData } from 'utils/constants';
import {
  allTypesData,
  checkDateRange,
  downloadFile,
  getFilteredColor,
  typeFilterToTypeFilterDto,
} from 'utils/functions';
import { DetailsButtons } from './DetailIcons';

export const Repair: React.FC = () => {
  const dispatch = useAppDispatch();
  const { globalDate, globalDateUpdate, globalPeriod } = useAppSelector(
    state => state.globalDataSlicer,
  );

  const [showDetails, setShowDetails] = useState<{
    show: boolean;
    id: string;
  }>({ show: false, id: '' });
  const [showItens, setShowItens] = useState<{
    show: boolean;
    id: string;
  }>({ show: false, id: '' });

  const tableLabels: TableLabel[] = useMemo(
    () => [
      {
        title: 'Nº de atendimento',
        type: 'data',
        accessor: 'id',
      },
      {
        title: 'Placas',
        type: 'data',
        accessor: 'plate',
      },
      {
        title: 'Status do atendimento',
        type: 'text',
        accessor: 'status',
      },
      {
        title: 'Motivo',
        type: 'text',
        accessor: 'type',
      },
      {
        title: 'Data de Agendamento',
        type: 'text',
        accessor: 'scheduleDate',
      },
      {
        title: 'Data de Previsão',
        type: 'text',
        accessor: 'previsionDate',
      },
      {
        title: 'Data de Saída',
        type: 'text',
        accessor: 'exitDate',
      },
      {
        title: 'Situação do Veículo',
        type: 'text',
        accessor: 'situation',
      },
      {
        title: 'Mais Detalhes',
        type: 'custom',
        disableSorting: true,
        component: id => (
          <DetailsButtons
            setShowDetails={setShowDetails}
            setShowItens={setShowItens}
            id={id}
          />
        ),
      },
    ],
    [],
  );

  const [pickedPeriod, setPickedPeriod] = useState(globalPeriod);
  const [dateRange, setDateRange] = useState(checkDateRange(globalDate));
  const [typeFilter, setTypeFilter] = useState<ChartTypeFilter>(
    ChartTypeFilters[0],
  );
  const [chartLabels, setChartLabels] = useState(['']);
  const [chartData, setChartData] = useState<ChartData[] | null>(null);

  const {
    data: chartValues,
    isLoading: isLoadingChart,
    isFetching: isFetchingChart,
    isError: isErrorChart,
    refetch: refetchChart,
  } = useGetRepairChartDataQuery({
    dimension: pickedPeriod,
    type: typeFilterToTypeFilterDto(typeFilter.type),
    startDate: format(
      checkDateRange(dateRange)[0].startDate || new Date(),
      'yyyy-MM-dd',
    ),
    endDate: format(
      checkDateRange(dateRange)[0].endDate || new Date(),
      'yyyy-MM-dd',
    ),
  });

  const {
    data: multiInfoData,
    isLoading: isLoadingMultiInfo,
    isFetching: isFetchingMultiInfo,
    isError: isErrorMultiInfo,
    refetch: refetchMultiInfo,
  } = useGetRepairChartDataQuery({
    dimension: pickedPeriod,
    startDate: format(
      checkDateRange(dateRange)[0].startDate || new Date(),
      'yyyy-MM-dd',
    ),
    endDate: format(
      checkDateRange(dateRange)[0].endDate || new Date(),
      'yyyy-MM-dd',
    ),
  });

  const [
    fetchTable,
    {
      data: tableData,
      isLoading: isLoadingTable,
      isFetching: isFetchingTable,
      isError: isErrorTable,
    },
  ] = useLazyGetRepairTableDataQuery();

  const { handleChartData, handleChartLabels } = useChart();

  useEffect(() => {
    fetchTable({
      page: 1,
      quantity: 5,
      term: '',
      endDate: format(
        checkDateRange(dateRange)[0].endDate || new Date(),
        'yyyy-MM-dd',
      ),
      startDate: format(
        checkDateRange(dateRange)[0].startDate || new Date(),
        'yyyy-MM-dd',
      ),
    });
  }, []);

  useEffect(() => {
    setPickedPeriod(globalPeriod);
    if (globalDateUpdate) {
      setDateRange(globalDate);
      dispatch(updatesGlobalDate(false));
    }
  }, [globalDate, globalDateUpdate]);

  useEffect(() => {
    setPickedPeriod(globalPeriod);
    setDateRange(globalDate);
  }, [globalPeriod]);

  useEffect(() => {
    if (chartValues) {
      const labels = handleChartLabels(
        pickedPeriod,
        chartValues,
        dateRange[0].startDate,
        dateRange[0].endDate,
      );

      setChartLabels(labels);

      const chart = handleChartData(
        labels,
        typeFilter.type === 'total'
          ? allTypesData(chartValues.itens, labels)
          : [
              {
                label: 'Quantidade',
                values: chartValues.itens,
                fill: {
                  type: 'start',
                  color: getFilteredColor(typeFilter?.type),
                },
                accessor: 'quantity',
              },
            ],
      );

      setChartData(chart);
    }
  }, [chartValues]);

  return (
    <DelayAnimation>
      <VStack
        px={{ base: '16px', md: '32px' }}
        pb="20px"
        flex={1}
        minH="full"
        spacing="20px"
        fontSize="xl"
      >
        <DetailsModal
          isOpen={showDetails.show}
          onClose={() => setShowDetails({ show: false, id: '' })}
          id={showDetails.id}
        />
        <ItensModal
          isOpen={showItens.show}
          onClose={() => setShowItens({ show: false, id: '' })}
          id={showItens.id}
        />
        <ChartCard
          cardName="repair"
          pickerType="date"
          picker={['Dia', 'Mês']}
          firstPicked={globalPeriod}
          setPicked={setPickedPeriod}
          startDate={dateRange}
          setDateRange={setDateRange}
          setFilter={setTypeFilter}
          filters={ChartTypeFilters}
          w="100%"
          subtitleMb="20px"
          customTitle={
            <Text
              alignSelf="center"
              color="text.100"
              fontSize="16px"
              fontWeight={400}
            >
              Gráfico de <b>Serviços</b>
            </Text>
          }
          subtitle="Comparativo com os principais serviços realizados. Se preferir filtre pelos serviços abaixo"
          isError={isErrorChart}
          refetch={refetchChart}
        >
          <Flex w="100%" overflow="auto" justify="center">
            <BarChart
              stackedY={true}
              stackedX={true}
              loading={isLoadingChart || isFetchingChart}
              w="800px"
              labels={chartLabels}
              data={chartData || emptyData}
            />
          </Flex>
        </ChartCard>

        <MultiInfoCard
          loading={isLoadingMultiInfo || isFetchingMultiInfo}
          w="100%"
          title="Atendimentos Realizados"
          subtitle="Quantidade por tipo de serviço"
          picker={[]}
          data={multiInfoData}
          showTotal={true}
          isError={isErrorMultiInfo}
          refetch={refetchMultiInfo}
        />
        <TableCard
          fetchDownload={async () => await downloadFile('repair', 'Servicos')}
          hasGlobalSearch
          loading={isLoadingTable || isFetchingTable}
          customTitle={
            <Text
              alignSelf="center"
              color="text.100"
              fontSize="16px"
              fontWeight={400}
            >
              Relatório de Veículos
            </Text>
          }
          analyticsId="Veículos"
          subtitle="Listagem de veículos que passaram por serviços"
          overflowX="auto"
          w="100%"
          labels={tableLabels}
          startDate={format(
            checkDateRange(dateRange)[0].startDate || new Date(),
            'yyyy-MM-dd',
          )}
          endDate={format(
            checkDateRange(dateRange)[0].endDate || new Date(),
            'yyyy-MM-dd',
          )}
          fetchData={fetchTable}
          data={tableData}
          isError={isErrorTable}
          refetch={fetchTable}
        />
      </VStack>
    </DelayAnimation>
  );
};
